import { useState } from "react";
import type { Style } from "@/components/api/companiesApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

interface StyleSectionProps {
    style: Style;
    onChange: (style: Style) => void;
}

export function StyleSection({ style, onChange }: StyleSectionProps) {
    // fontStyle is free text -> commit on blur; colors/corners commit on change.
    const [fontStyle, setFontStyle] = useState(style.fontStyle);

    const update = (patch: Partial<Style>) => onChange({ ...style, ...patch });

    return (
        <section className="glass-card p-5 space-y-4">
            <div>
                <h2 className="font-semibold">Brand style</h2>
                <p className="text-xs text-muted-foreground">
                    Reference only — captured for context, never applied to the app.
                </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
                <ColorField
                    id="style-primary"
                    label="Primary color"
                    value={style.primaryColor}
                    onChange={(primaryColor) => update({ primaryColor })}
                />
                <ColorField
                    id="style-secondary"
                    label="Secondary color"
                    value={style.secondaryColor}
                    onChange={(secondaryColor) => update({ secondaryColor })}
                />
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="style-font">Font style</Label>
                <Input
                    id="style-font"
                    value={fontStyle}
                    onChange={(e) => setFontStyle(e.target.value)}
                    onBlur={() => {
                        const trimmed = fontStyle.trim();
                        if (trimmed !== style.fontStyle) {
                            setFontStyle(trimmed);
                            update({ fontStyle: trimmed });
                        }
                    }}
                    placeholder="e.g. Poppins / sans-serif"
                />
            </div>

            <div className="space-y-2">
                <Label>Corners</Label>
                <RadioGroup
                    value={style.corners}
                    onValueChange={(v) => update({ corners: v as Style["corners"] })}
                    className="flex gap-6"
                >
                    <div className="flex items-center gap-2">
                        <RadioGroupItem value="rounded" id="corners-rounded" />
                        <Label htmlFor="corners-rounded" className="flex items-center gap-2 font-normal cursor-pointer">
                            <span className="h-5 w-5 rounded-md border-2 border-muted-foreground/60" />
                            Rounded
                        </Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <RadioGroupItem value="sharp" id="corners-sharp" />
                        <Label htmlFor="corners-sharp" className="flex items-center gap-2 font-normal cursor-pointer">
                            <span className="h-5 w-5 rounded-none border-2 border-muted-foreground/60" />
                            Sharp
                        </Label>
                    </div>
                </RadioGroup>
            </div>

            <StylePreview style={{ ...style, fontStyle }} />
        </section>
    );
}

interface ColorFieldProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
}

function ColorField({ id, label, value, onChange }: ColorFieldProps) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            <div className="flex items-center gap-2">
                <input
                    id={id}
                    type="color"
                    value={value || "#000000"}
                    onChange={(e) => onChange(e.target.value)}
                    className={cn(
                        "h-9 w-12 cursor-pointer rounded-md border border-input bg-background p-1",
                        !value && "opacity-70",
                    )}
                />
                <span className="text-sm text-muted-foreground min-w-16">
                    {value || "Not set"}
                </span>
                {value && (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-xs"
                        onClick={() => onChange("")}
                    >
                        Clear
                    </Button>
                )}
            </div>
        </div>
    );
}

function StylePreview({ style }: { style: Style }) {
    const border = style.primaryColor || "hsl(var(--border))";
    const accent = style.secondaryColor || style.primaryColor || "hsl(var(--muted-foreground))";
    return (
        <div>
            <p className="text-xs text-muted-foreground mb-1.5">Preview</p>
            <div
                className={cn(
                    "p-4 border-2 bg-background/40",
                    style.corners === "rounded" ? "rounded-xl" : "rounded-none",
                )}
                style={{ borderColor: border, fontFamily: style.fontStyle || undefined }}
            >
                <div className="flex items-center gap-2">
                    <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: style.primaryColor || "transparent", outline: style.primaryColor ? "none" : "1px dashed currentColor" }}
                    />
                    <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: style.secondaryColor || "transparent", outline: style.secondaryColor ? "none" : "1px dashed currentColor" }}
                    />
                    <span className="text-sm font-semibold" style={{ color: accent }}>
                        Sample heading
                    </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                    {style.fontStyle || "Default font"} · {style.corners} corners
                </p>
            </div>
        </div>
    );
}
